import { inject, Injectable, OnDestroy } from '@angular/core';
import * as Y from 'yjs';
import { Canvas } from 'fabric';
import { Subscription } from 'rxjs';
import {
    BackgroundFit,
    EphemeralApplyHandler,
    FABRIC_ORIGIN,
    FabricYjsBinding,
    hideAsStale,
} from 'shared/util-yjs-fabric';
import { snapshotDrawingObjects, type SerializedFabricObject } from 'shared/fabric-drawing/tools';
import { PCP_LOGGER, PcpLoggerInterface } from '@pcp/logging';
import { LessonYjsService } from './lesson-yjs.service';
import { SyncedLessonState } from '../../public/yjs/lesson-ydoc-schema';

type Layer = 1 | 2 | 3;

/**
 * Per-layer drawing-canvas state. The `canvas` is owned by the UI (renderer) and
 * lives as long as the preview component is mounted; the `binding`/`undoManager`
 * track the CURRENT Y.Doc and are torn down + recreated whenever the lesson Y.Doc
 * lifecycle flips (offline draft ↔ active lesson, reconnect) — see `hydrated$`.
 */
interface LayerDrawingState {
    canvas: Canvas;
    binding?: FabricYjsBinding;
    undoManager?: Y.UndoManager;
    /** Last locally-set background URL; re-applied on rebind (a new binding doesn't know it). */
    background: string | null;
    /** Fit mode for {@link background}; re-applied on rebind. Defaults to 'fill'. */
    backgroundFit: BackgroundFit;
    /** Registered ephemeral (awareness) handlers; re-registered on rebind. */
    ephemeral: Map<string, EphemeralApplyHandler>;
    /** Optional post-reconcile hook (content-band re-placement); re-applied on rebind. */
    afterReconcile?: () => void;
    /**
     * Showing id whose rejection has already been logged, so the warn fires once per
     * showing. Needs no reset: each showing id is a fresh UUID, so the next showing's
     * first rejection compares unequal and warns again.
     */
    loggedRejectionShowingId?: string;
    /** Showing id under which an unstamped fail-open pass was logged — same dedupe scheme. */
    loggedUnstampedShowingId?: string;
    /** Whether the current "drawing with no showing" episode was already warned about. */
    loggedNoShowingWarn?: boolean;
}

/**
 * LessonDrawingService — owns one `FabricYjsBinding` (+ `Y.UndoManager`) per attached
 * layer canvas, over the lesson Y.Doc's `drawing-objects-layer-{1,2,3}` arrays.
 *
 * @internal Use `ClassroomFacade`'s drawing-canvas handle for the public API.
 *
 * Decision #1b (`migrate-slides-to-fabric-canvas` design): the canvas↔Yjs binding is
 * owned inside `classroom/data-access` (mirroring PT's `PracticeTrainerYjsService`), so
 * raw Yjs handles never leak through the public facade. The renderer creates the
 * `fabric.Canvas` and hands it in; this service binds it to sync.
 *
 * Lifecycle: the Y.Doc is destroyed + recreated on lesson start while the preview
 * component (and its canvas) stay mounted. We re-bind on `LessonYjsService.hydrated$`:
 * `true` → (re)create bindings over the current arrays; `false` → tear down bindings
 * (keep the canvas refs so they rebind on the next hydration).
 *
 * ## Showing identity (`slide-showing-identity`)
 *
 * Every drawing created locally is stamped with the layer's current `showingId`, and a
 * drawing whose stamp does not match the current showing is never shown. Enforced at two
 * points, because neither covers the other:
 * - `shouldRender` — objects arriving from Y state are added **hidden** when stale (they
 *   must still occupy their array slot; see the callback's docs).
 * - {@link sweepStaleDrawings} — runs on a showing transition and reaches what the filter
 *   cannot: objects absent from the array, and objects added locally on this very client.
 *
 * Unstamped objects always pass (fail-open) — a mid-lesson deploy leaves Redis-persisted
 * drawings without stamps, and erasing a live lesson's work is worse than the leak.
 *
 * **Contract for a future live-stroke preview (`drawingStroke` awareness field, not yet
 * implemented):** the receiver half is enforced structurally — {@link registerEphemeral}
 * drops any payload stamped with a superseded `showingId` before the handler runs. What a
 * publisher MUST still do itself: stamp the payload with the layer's current `showingId`
 * (an unstamped payload passes the guard fail-open and would land on whatever slide is
 * showing — breach ③, which neither enforcement point above can see), and commit the
 * finished stroke to the Y.Array *before* clearing the awareness payload, per the
 * established commit-before-clear ordering.
 */
@Injectable({ providedIn: 'root' })
export class LessonDrawingService implements OnDestroy {
    private readonly lessonYjs = inject(LessonYjsService);
    private readonly log: PcpLoggerInterface = inject(PCP_LOGGER).createScoped('yjs-drawing');

    private readonly layers = new Map<Layer, LayerDrawingState>();
    private readonly hydratedSub: Subscription;
    /** Showing-transition watcher; re-created per hydration (see {@link watchShowingChanges}). */
    private showingSub?: Subscription;
    /** One pending deferred sweep at a time (see {@link scheduleSweep}). */
    private sweepScheduled = false;

    constructor() {
        // App-lifetime subscription (root service): rebind drawing canvases whenever the
        // lesson Y.Doc binding becomes ready / is torn down.
        this.hydratedSub = this.lessonYjs.hydrated$.subscribe((hydrated) => {
            if (hydrated) {
                this.bindAllLayers();
                this.watchShowingChanges();
            } else {
                this.unwatchShowingChanges();
                this.unbindAllLayers();
            }
        });
    }

    /**
     * Watch for slide-showing transitions and sweep each layer's canvas
     * (`slide-showing-identity`). Re-subscribed on every hydration because `changes$` belongs
     * to the current document's binding and does not survive a Y.Doc swap.
     */
    private watchShowingChanges(): void {
        this.unwatchShowingChanges();
        const changes$ = this.lessonYjs.changes$;
        if (!changes$) return;
        this.showingSub = changes$.subscribe((partial) => {
            if (!LessonDrawingService.touchesShowingId(partial)) return;
            this.scheduleSweep();
        });
    }

    private unwatchShowingChanges(): void {
        this.showingSub?.unsubscribe();
        this.showingSub = undefined;
    }

    private static touchesShowingId(partial: Partial<SyncedLessonState>): boolean {
        return 'primarySlideShowingId' in partial
            || 'secondarySlideShowingId' in partial
            || 'tertiarySlideShowingId' in partial;
    }

    /**
     * Attach a UI-owned fabric canvas to a layer for drawing sync. If a lesson Y.Doc is
     * already live, binds immediately; otherwise binds on the next hydration. Re-attaching
     * a layer (canvas swap) unbinds the previous canvas first.
     */
    attachCanvas(layer: Layer, canvas: Canvas): void {
        const existing = this.layers.get(layer);
        if (existing) {
            this.unbindLayer(layer);
            existing.canvas = canvas;
        } else {
            this.layers.set(layer, { canvas, background: null, backgroundFit: 'fill', ephemeral: new Map() });
        }
        if (this.lessonYjs.isInitialized()) {
            this.bindLayer(layer);
        }
    }

    /** Detach a layer's canvas: tear down its binding/undo and forget it. */
    detachCanvas(layer: Layer): void {
        this.unbindLayer(layer);
        this.layers.delete(layer);
    }

    undo(layer: Layer): void {
        this.layers.get(layer)?.undoManager?.undo();
    }

    redo(layer: Layer): void {
        this.layers.get(layer)?.undoManager?.redo();
    }

    canUndo(layer: Layer): boolean {
        return this.layers.get(layer)?.undoManager?.canUndo() ?? false;
    }

    canRedo(layer: Layer): boolean {
        return this.layers.get(layer)?.undoManager?.canRedo() ?? false;
    }

    /**
     * Snapshot a layer's exportable drawing objects (serialized fabric JSON, excluding the
     * background and any non-transfer overlays). Returns `[]` if no canvas is attached.
     * Used by client-side image export to rebuild the slide on an offscreen canvas.
     */
    snapshotObjects(layer: Layer): SerializedFabricObject[] {
        const canvas = this.layers.get(layer)?.canvas;
        return canvas ? snapshotDrawingObjects(canvas) : [];
    }

    /** Apply a background URL to this layer's canvas locally (per-client variant; not synced). */
    setBackgroundLocal(layer: Layer, url: string | null, fit: BackgroundFit = 'fill'): void {
        const state = this.layers.get(layer);
        if (!state) return;
        state.background = url;
        state.backgroundFit = fit;
        state.binding?.setBackgroundLocal(url, fit);
    }

    /** Clear all drawing objects for a layer (slide reset) — reconciles every client's canvas. */
    clearDrawings(layer: Layer): void {
        this.lessonYjs.clearDrawingObjects(layer);
    }

    /** Publish an ephemeral (awareness) payload for a layer — e.g. an in-progress stroke preview. */
    publishEphemeral(layer: Layer, field: string, payload: unknown | null): void {
        this.layers.get(layer)?.binding?.publishEphemeral(field, payload);
    }

    /**
     * Register a remote-ephemeral handler for a layer (e.g. render a remote client's
     * in-progress stroke). Remembered so it survives a rebind.
     *
     * Showing guard (`slide-showing-identity`, breach ③): a payload that records a
     * `showingId` differing from the layer's current showing is dropped before the handler
     * runs — an in-flight stroke crossing a slide change must not land on the new slide,
     * and neither `shouldRender` nor the sweep can see awareness data. Payloads without a
     * `showingId` pass through unchanged (fail-open, Decision 7), as do `null` (clear) and
     * non-object payloads.
     */
    registerEphemeral(layer: Layer, field: string, apply: EphemeralApplyHandler): void {
        const state = this.layers.get(layer);
        if (!state) return;
        const guarded: EphemeralApplyHandler = (payload, clientId) => {
            if (this.isFromSupersededShowing(layer, payload)) {
                return false; // dropped — nothing was drawn, no re-render needed
            }
            return apply(payload, clientId);
        };
        state.ephemeral.set(field, guarded);
        state.binding?.registerEphemeralField(field, guarded);
    }

    /** Is `payload` stamped with a non-empty `showingId` that differs from the layer's current showing? */
    private isFromSupersededShowing(layer: Layer, payload: unknown): boolean {
        if (payload === null || typeof payload !== 'object') return false;
        const stamp = (payload as { showingId?: unknown }).showingId;
        if (typeof stamp !== 'string' || stamp === '') return false;
        const current = this.lessonYjs.getShowingId(layer);
        return !!current && stamp !== current;
    }

    /**
     * Register (or clear, with `undefined`) a post-reconcile hook for a layer — fired after
     * `syncFromYjs` re-adds drawings, so a content capability can re-place its objects below
     * them. Remembered so it survives a rebind.
     */
    setAfterReconcile(layer: Layer, handler: (() => void) | undefined): void {
        const state = this.layers.get(layer);
        if (!state) return;
        state.afterReconcile = handler;
        state.binding?.setAfterReconcile(handler);
    }

    private bindAllLayers(): void {
        for (const layer of this.layers.keys()) {
            this.bindLayer(layer);
        }
    }

    private unbindAllLayers(): void {
        for (const layer of this.layers.keys()) {
            this.unbindLayer(layer);
        }
    }

    private bindLayer(layer: Layer): void {
        const state = this.layers.get(layer);
        if (!state || state.binding) return; // not attached, or already bound

        const objects = this.lessonYjs.getDrawingObjectsArray(layer);
        const metadata = this.lessonYjs.getDrawingMetadataMap(layer);
        const yDoc = this.lessonYjs.getYDoc();
        if (!objects || !metadata || !yDoc) {
            this.log.warn('bindLayer: Y.Doc handles unavailable — skipping', { layer });
            return;
        }

        // Showing-identity enforcement (`slide-showing-identity`). Both hooks are read-through
        // to the Y.Map — never a cached showing id — so a rebind onto a different document
        // needs no re-priming. See `SyncedLessonState.primarySlideShowingId`.
        const binding = new FabricYjsBinding(state.canvas, objects, metadata, {
            stampLocalObject: () => {
                const showingId = this.lessonYjs.getShowingId(layer);
                if (!showingId) {
                    // No showing on this layer yet (pre-lesson draft before any slide was
                    // shown). Stamping '' would be indistinguishable from "unstamped", which
                    // fails open anyway — so contribute nothing and let that path handle it.
                    //
                    // Logged because an unstamped local drawing is the quiet failure mode of
                    // this whole feature: fail-open (Decision 7) means a stamp path that
                    // silently stops working turns the guard into a no-op rather than
                    // breaking anything visibly. Expected before the first slide is shown;
                    // anything else is a bug. Deduped per no-showing episode — N strokes on
                    // a draft canvas carry no more information than the first.
                    if (!state.loggedNoShowingWarn) {
                        state.loggedNoShowingWarn = true;
                        this.log.warn(
                            'Local drawing created with no slide showing — left unstamped (guard inactive for it)',
                            { layer },
                        );
                    }
                    return undefined;
                }
                state.loggedNoShowingWarn = false; // a showing exists again — re-arm the episode warn
                return { showingId };
            },
            shouldRender: (data) => this.belongsToCurrentShowing(layer, data['showingId']),
        }, this.log);
        const undoManager = new Y.UndoManager(objects, {
            trackedOrigins: new Set([FABRIC_ORIGIN]),
        });

        const awareness = this.lessonYjs.getAwareness();
        if (awareness) {
            // `clearWhenEmpty`: an idle classroom must emit no awareness keep-alive. The
            // three layer bindings share this one awareness instance, so the publish path
            // only nulls the state when NO layer has an ephemeral field left.
            //
            // The constructor-seeded `{}` is neutralised at document scope in
            // `LessonYjsService` (right after the provider is built) rather than here — a
            // client with no slide canvas mounted never reaches this code and would
            // otherwise keep renewing the seed.
            binding.setAwareness(awareness, { clearWhenEmpty: true });
        }
        // Restore consumer-registered ephemeral handlers + the local background.
        for (const [field, apply] of state.ephemeral) {
            binding.registerEphemeralField(field, apply);
        }
        if (state.afterReconcile) {
            binding.setAfterReconcile(state.afterReconcile);
        }
        if (state.background !== null) {
            binding.setBackgroundLocal(state.background, state.backgroundFit);
        }

        state.binding = binding;
        state.undoManager = undoManager;
    }

    /**
     * Does a drawing carrying `stamp` belong to this layer's current showing?
     *
     * **Fails open on an absent stamp** (Decision 7): objects persisted in Redis before this
     * feature shipped, and objects created before any slide was shown, carry none — failing
     * closed would erase a live lesson's drawings, a far worse outcome than the leak this
     * guards against. Both breaches this defends produce *stamped* objects, so the guard
     * still fires exactly where it is needed. Per spec, the fail-open pass is reported to
     * the logs — once per showing, since a pre-deploy batch can be dozens of objects and
     * this sits on the reconcile path.
     *
     * Also fails open while the layer has no showing id at all, so nothing is hidden before
     * the first slide is shown.
     */
    private belongsToCurrentShowing(layer: Layer, stamp: unknown): boolean {
        const current = this.lessonYjs.getShowingId(layer);
        if (typeof stamp !== 'string' || stamp === '') {
            const state = this.layers.get(layer);
            if (current && state && state.loggedUnstampedShowingId !== current) {
                state.loggedUnstampedShowingId = current;
                this.log.warn('Unstamped drawing rendered as belonging to the current showing (fail-open)', {
                    layer, currentShowingId: current,
                });
            }
            return true;
        }
        if (!current) return true;
        if (stamp === current) return true;

        const state = this.layers.get(layer);
        // First direct evidence that a wrong-showing drawing reached this client. Deduped per
        // showing: a stale batch can be dozens of objects and this sits on the reconcile path.
        if (state && state.loggedRejectionShowingId !== current) {
            state.loggedRejectionShowingId = current;
            this.log.warn('Drawing from a superseded slide showing rejected', {
                layer, stamp, currentShowingId: current,
            });
        }
        return false;
    }

    /**
     * Remove canvas objects belonging to a superseded showing (`slide-showing-identity`).
     * Runs deferred via {@link scheduleSweep}, never inside a Yjs observer dispatch.
     *
     * This is the enforcement point the state filter cannot cover: it acts on the *canvas*,
     * so it reaches objects that are not in the drawing Y.Array at all (untracked after a
     * document swap) and objects added locally on the very client that drew them — a slide
     * change arrives there as an incremental delete delta, never a reconcile, so
     * `shouldRender` is never consulted for them.
     *
     * Skips `_yjsEphemeral` objects: in-progress shape/arrow previews are deliberately never
     * stamped (the stamp hook sits after the binding's ephemeral guard), and sweeping them
     * would make a shape vanish mid-gesture if the showing changed during the drag. Objects
     * with no stamp are left alone, consistent with {@link belongsToCurrentShowing}.
     *
     * **Stale objects that are STILL IN the drawing Y.Array are hidden, not removed** — the
     * same constraint that makes `shouldRender` hide rather than skip. `handleYArrayEvent`
     * maps Y.Array indices onto canvas objects positionally through `_yjsId`-bearing
     * objects, so removing one that the array still contains would shift that index space
     * and make a later partial delete (DEL key, undo) hit the wrong object. Only objects the
     * array does *not* contain — untracked leftovers, the breach this sweep exists for — are
     * actually removed, which restores alignment rather than breaking it.
     */
    private sweepStaleDrawings(layer: Layer): void {
        const state = this.layers.get(layer);
        if (!state) return;
        const current = this.lessonYjs.getShowingId(layer);
        if (!current) return;

        const stale = state.canvas.getObjects().filter((obj) => {
            const o = obj as { _yjsEphemeral?: boolean; showingId?: unknown };
            if (o._yjsEphemeral) return false;
            return typeof o.showingId === 'string' && o.showingId !== '' && o.showingId !== current;
        });
        if (stale.length === 0) return;

        // Ids the drawing array still holds — these must keep a canvas slot.
        const trackedIds = new Set<string>();
        for (const yMap of this.lessonYjs.getDrawingObjectsArray(layer)?.toArray() ?? []) {
            const id = yMap.get('_yjsId');
            if (typeof id === 'string') trackedIds.add(id);
        }

        let removed = 0;
        let hidden = 0;
        for (const obj of stale) {
            const id = (obj as { _yjsId?: string })._yjsId;
            if (id !== undefined && trackedIds.has(id)) {
                // Already-hidden survivors (hidden by shouldRender or an earlier sweep) are
                // skipped so a lingering tracked survivor is reported once, not on every
                // subsequent sweep pass until the next clear finally deletes it.
                if (obj.visible !== false) {
                    hideAsStale(obj);
                    hidden++;
                }
            } else {
                state.canvas.remove(obj);
                removed++;
            }
        }
        if (removed === 0 && hidden === 0) return;
        state.canvas.requestRenderAll();
        this.log.warn('Drawings from a superseded slide showing swept off the canvas', {
            layer, removed, hidden, currentShowingId: current,
        });
    }

    /**
     * Defer the sweep out of the current Yjs observer dispatch
     * (`slide-showing-identity-followups`, bug 2).
     *
     * `changes$` emits synchronously from the lesson Y.Map observer, INSIDE the event
     * dispatch of the transaction that changed the showing — and that same transaction
     * normally also cleared the drawing Y.Array (every `setSlide` does). Cross-type
     * observer ordering within one transaction is not guaranteed, so sweeping here could
     * remove canvas objects that the binding's pending positional delete delta still
     * counts, misaligning canvas and array (the e228d6b78 failure class). All of a
     * transaction's observers run synchronously, so a microtask is guaranteed to run
     * after the binding has consumed the array delta. Coalesced: N showing-key emissions
     * in one dispatch → one sweep pass.
     */
    private scheduleSweep(): void {
        if (this.sweepScheduled) return;
        this.sweepScheduled = true;
        queueMicrotask(() => {
            this.sweepScheduled = false;
            this.sweepAllLayers();
        });
    }

    /**
     * Sweep every attached layer. Stateless by design: {@link sweepStaleDrawings} is
     * stamp-guarded and idempotent, so sweeping a layer whose showing did not change is a
     * cheap no-op pass — simpler than tracking per-layer transitions, and immune to their
     * seeding gaps (a rebind or late-attached layer needs no priming; carried drawings
     * ride the same snapshot as the carried showing id, so they are never stale). Driven
     * by `changes$` rather than by the write methods: the showing can change because of a
     * REMOTE write just as easily as a local one, and both must sweep. A layer detached
     * (or the whole service destroyed) between scheduling and the microtask simply no
     * longer appears in `this.layers`.
     */
    private sweepAllLayers(): void {
        for (const layer of this.layers.keys()) {
            this.sweepStaleDrawings(layer);
        }
    }

    private unbindLayer(layer: Layer): void {
        const state = this.layers.get(layer);
        if (!state) return;
        state.binding?.destroy();
        state.undoManager?.destroy();
        state.binding = undefined;
        state.undoManager = undefined;
    }

    ngOnDestroy(): void {
        this.hydratedSub.unsubscribe();
        this.unwatchShowingChanges();
        this.unbindAllLayers();
        this.layers.clear();
    }
}
